(() => {
  const root = document.querySelector('[data-dagger-wholesale]');
  const catalog = root?.querySelector('[data-wholesale-catalog]');
  if (!root || !catalog) return;

  const QUANTITY_KEY = 'dagger-wholesale-order-quantities';
  const LAST_URL_KEY = 'dagger-wholesale-last-portal-url';
  const tabs = [...root.querySelectorAll('[data-category-tab]')];
  const panels = [...root.querySelectorAll('[data-category-panel]')];
  const inputs = [...root.querySelectorAll('[data-quantity-input]')];
  const search = root.querySelector('[data-wholesale-search]');
  const clearSearch = root.querySelector('[data-wholesale-clear-search]');
  const addButtons = [...root.querySelectorAll('[data-wholesale-add]')];
  const status = root.querySelector('[data-wholesale-status]');
  const portalPath = root.dataset.portalPath;

  const safeRead = (key, fallback = {}) => {
    try { return JSON.parse(sessionStorage.getItem(key)) || fallback; } catch (_) { return fallback; }
  };
  const safeWrite = (key, value) => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const quantities = safeRead(QUANTITY_KEY);

  const normalizeQuantity = (value) => Math.max(0, Math.min(9999, Number.parseInt(value, 10) || 0));
  const persistInput = (input) => {
    const quantity = normalizeQuantity(input.value);
    input.value = quantity;
    if (quantity === 0) delete quantities[input.dataset.variantId];
    else quantities[input.dataset.variantId] = quantity;
    safeWrite(QUANTITY_KEY, quantities);
    input.closest('[data-variant-row]')?.classList.toggle('is-selected', quantity > 0);
    updateSelectedState();
  };
  const updateSelectedState = () => {
    const total = inputs.reduce((sum, input) => sum + normalizeQuantity(input.value), 0);
    addButtons.forEach((button) => {
      button.disabled = total === 0;
      const count = button.querySelector('[data-wholesale-selected-count]');
      if (count) count.textContent = `(${total})`;
    });
  };

  inputs.forEach((input) => {
    input.value = normalizeQuantity(quantities[input.dataset.variantId]);
    input.closest('[data-variant-row]')?.classList.toggle('is-selected', input.value !== '0');
    input.addEventListener('input', () => persistInput(input));
    input.addEventListener('change', () => persistInput(input));
  });
  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!button) return;
    const input = button.parentElement.querySelector('[data-quantity-input]');
    if (!input || input.disabled) return;
    const change = button.hasAttribute('data-quantity-plus') ? 1 : -1;
    input.value = normalizeQuantity(Number(input.value) + change);
    persistInput(input);
  });

  const validCategory = (candidate) => tabs.some((tab) => tab.dataset.categoryTab === candidate);
  const selectCategory = (category, updateHistory = false) => {
    const selected = validCategory(category) ? category : tabs[0]?.dataset.categoryTab;
    if (!selected) { catalog.style.visibility = 'visible'; return; }
    tabs.forEach((tab) => {
      const active = tab.dataset.categoryTab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel) => { panel.hidden = panel.dataset.categoryPanel !== selected; });
    const url = new URL(window.location.href);
    url.searchParams.set('category', selected);
    if (updateHistory) history.pushState({ category: selected }, '', url);
    else if (url.href !== window.location.href) history.replaceState({ category: selected }, '', url);
    try { sessionStorage.setItem(LAST_URL_KEY, url.href); } catch (_) {}
    catalog.style.visibility = 'visible';
    filterProducts();
  };
  tabs.forEach((tab) => tab.addEventListener('click', () => selectCategory(tab.dataset.categoryTab, true)));
  window.addEventListener('popstate', () => selectCategory(new URL(window.location.href).searchParams.get('category')));

  const filterProducts = () => {
    const term = (search?.value || '').trim().toLowerCase();
    panels.forEach((panel) => {
      let shown = 0;
      panel.querySelectorAll('[data-wholesale-product]').forEach((product) => {
        const match = !term || product.dataset.searchText.includes(term);
        product.hidden = !match;
        if (match) shown += 1;
      });
      const noResults = panel.querySelector('[data-no-results]');
      if (noResults) noResults.hidden = shown !== 0;
    });
    if (clearSearch) clearSearch.disabled = term.length === 0;
  };
  search?.addEventListener('input', filterProducts);
  clearSearch?.addEventListener('click', () => {
    search.value = '';
    filterProducts();
    search.focus();
  });

  const localizedUrl = (path) => {
    if (path.startsWith('/')) return path;
    const shopRoot = window.Shopify?.routes?.root || '/';
    return `${shopRoot}${path}`.replace(/\/+/g, '/');
  };
  const updateCartCount = async () => {
    const response = await fetch(localizedUrl('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart state could not be refreshed.');
    const cart = await response.json();
    root.querySelectorAll('[data-wholesale-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
  };
  const addSelected = async () => {
    const submitted = inputs
      .map((input) => ({ input, id: Number(input.dataset.variantId), quantity: normalizeQuantity(input.value) }))
      .filter((item) => item.quantity > 0);
    if (!submitted.length) return;
    addButtons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    status.className = 'dagger-wholesale__status';
    status.textContent = 'Adding selected items…';
    try {
      const response = await fetch(localizedUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: submitted.map(({ id, quantity }) => ({ id, quantity })) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Selected items could not be added.');
      submitted.forEach(({ input, id }) => {
        input.value = 0;
        delete quantities[String(id)];
        input.closest('[data-variant-row]')?.classList.remove('is-selected');
      });
      safeWrite(QUANTITY_KEY, quantities);
      let cartCountUpdated = true;
      try { await updateCartCount(); } catch (_) { cartCountUpdated = false; }
      status.classList.add('is-success');
      status.textContent = cartCountUpdated
        ? 'Selected items were added to the cart.'
        : 'Selected items were added. Open the cart to confirm its current count.';
    } catch (error) {
      status.classList.add('is-error');
      status.textContent = error.message || 'Selected items could not be added. Please try again.';
    } finally {
      addButtons.forEach((button) => button.removeAttribute('aria-busy'));
      updateSelectedState();
    }
  };
  addButtons.forEach((button) => button.addEventListener('click', addSelected));

  updateSelectedState();
  selectCategory(new URL(window.location.href).searchParams.get('category'));
})();
